import Vue from 'vue';
import VueI18n from 'vue-i18n';

import en from './locales/en';
import es from './locales/es';

Vue.use(VueI18n);

export const SUPPORTED_LOCALES: string[] = ['en', 'es'];
export const FALLBACK_LOCALE = 'en';

// The locale is read from the `?locale=` query param, but the OAuth providers
// (and Firebase's `signInWithRedirect`) send the user away and back without it,
// so we keep it in sessionStorage. sessionStorage — and not localStorage — on
// purpose: the views call `localStorage.clear()` on mount.
const STORAGE_KEY = 'locale';
const QUERY_PARAM = 'locale';

/**
 * Reduces anything the URL may carry (`es`, `es-AR`, `ES_es`, ...) to one of
 * the supported locales, or null when we don't support it.
 */
function normalize(locale?: string | null): string | null {
	if (!locale) return null;
	const base = String(locale).toLowerCase().trim().split(/[-_]/)[0];
	return SUPPORTED_LOCALES.indexOf(base) !== -1 ? base : null;
}

function readFromQuery(): string | null {
	try {
		return normalize(new URLSearchParams(window.location.search).get(QUERY_PARAM));
	} catch (e) {
		return null;
	}
}

function readFromStorage(): string | null {
	try {
		return normalize(window.sessionStorage.getItem(STORAGE_KEY));
	} catch (e) {
		return null;
	}
}

function persist(locale: string): void {
	try {
		window.sessionStorage.setItem(STORAGE_KEY, locale);
	} catch (e) {
		// Private mode / storage disabled: the locale just won't survive a redirect.
	}
}

/**
 * URL query param first, then whatever we resolved before being redirected
 * away, then English.
 */
export function resolveLocale(): string {
	return readFromQuery() || readFromStorage() || FALLBACK_LOCALE;
}

const initialLocale = resolveLocale();

const i18n = new VueI18n({
	locale: initialLocale,
	fallbackLocale: FALLBACK_LOCALE,
	messages: { en, es },
	silentFallbackWarn: true,
});

export function setLocale(locale?: string | null): string {
	const next = normalize(locale) || FALLBACK_LOCALE;
	i18n.locale = next;
	persist(next);
	document.documentElement.setAttribute('lang', next);
	return next;
}

/**
 * Appends the active locale to a URL we send the user to, so the language
 * follows them into the other Rambox apps (which read the same query param).
 */
export function withLocale(url: string): string {
	if (!url) return url;
	// Appended textually on purpose: going through URLSearchParams would
	// re-encode the rest of the query (e.g. `%20` would come out as `+`).
	if (new RegExp(`[?&]${QUERY_PARAM}=`).test(url)) return url;
	const hashAt = url.indexOf('#');
	const path = hashAt === -1 ? url : url.slice(0, hashAt);
	const hash = hashAt === -1 ? '' : url.slice(hashAt);
	const separator = path.indexOf('?') === -1 ? '?' : '&';
	return `${path}${separator}${QUERY_PARAM}=${encodeURIComponent(i18n.locale)}${hash}`;
}

/**
 * Firebase errors come back in English no matter what. Map the known codes to
 * a translated message and fall back to whatever Firebase said.
 */
export function translateError(error: any, fallback?: string): string {
	const generic = () => fallback || (i18n.t('common.error') as string);
	if (!error) return generic();
	if (typeof error === 'string') return error;

	const key = error.code && `firebase.${error.code}`;
	if (key && (i18n.te(key) || i18n.te(key, FALLBACK_LOCALE))) return i18n.t(key) as string;

	return error.message || generic();
}

setLocale(initialLocale);

export default i18n;
